import { useTranslation } from 'react-i18next';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Text,
} from '@kognitos/lattice';
import { AccessDeniedAlert } from '@/shared/components/AccessDenied/AccessDeniedAlert';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';

/**
 * Props for {@link AssetDeleteDialog}.
 */
export interface IAssetDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** e.g. "Delete draft?". */
  title: string;
  /** What gets deleted, naming the asset. */
  description: string;
  /** A second line, e.g. "This can't be undone." */
  confirmation?: string;
  /** A blocking concern shown above the description, e.g. runs still in progress. */
  warning?: { title: string; description: string };
  /** The destructive button's label. */
  confirmLabel: string;
  /** The delete is in flight: the button spins and the dialog can't be dismissed. */
  isPending?: boolean;
  /** Keeps the button disabled while something the delete depends on loads. */
  isCheckingPreconditions?: boolean;
  /** The failed delete. A 403 shows the shared access-denied alert. */
  error?: unknown;
  /** Shown for any other failure. */
  errorMessage: string;
  /** Keeps clicks inside the dialog from reaching a card behind it. */
  stopPropagation?: boolean;
  'data-testid'?: string;
}

/**
 * The delete confirmation for every asset: automations, drafts, agents and
 * context graphs share its wording, layout, pending state and error handling.
 */
export function AssetDeleteDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmation,
  warning,
  confirmLabel,
  isPending = false,
  isCheckingPreconditions = false,
  error,
  errorMessage,
  stopPropagation,
  'data-testid': dataTestId = 'asset-delete-dialog',
}: IAssetDeleteDialogProps) {
  const { t } = useTranslation('common');
  const isBusy = isPending || isCheckingPreconditions;

  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        if (!value && !isPending) onClose();
      }}
    >
      <DialogContent data-testid={dataTestId} stopPropagation={stopPropagation}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Flex direction="column" gap={4}>
          {warning && (
            <Alert variant="destructive">
              <AlertTitle>{warning.title}</AlertTitle>
              <AlertDescription>{warning.description}</AlertDescription>
            </Alert>
          )}
          <DialogDescription asChild>
            <Text color="muted" level="small">
              {description}
            </Text>
          </DialogDescription>
          {confirmation && (
            <Text color="muted" level="small">
              {confirmation}
            </Text>
          )}
          {Boolean(error) &&
            (checkIsAccessDeniedError(error) ? (
              <AccessDeniedAlert />
            ) : (
              <Alert variant="destructive">
                <AlertTitle>{t('common.error.title')}</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ))}
        </Flex>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
            data-testid={`${dataTestId}-cancel`}
          >
            {t('common.action.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isBusy}
            isLoading={isPending}
            data-testid={`${dataTestId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
